import {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useApolloClient } from '@apollo/client';
import Toast from 'react-native-root-toast';
import moment from 'moment';

import { valueExists } from '../../../common/utils/utils';
import { serializeDateKeyForTimezone } from '../../../common/utils/warehousing/formatExpiryBatchSummary';
import { GET_WAREHOUSE_PRODUCT_EXPIRY_BATCHES } from '../../../common/graphql/warehouseProduct';

import {
	getCurrentWarehouseBatchRows,
	getStocktakeBatchRowsForDialog,
	getWarehouseProductTimezone,
	normalizeStocktakeBatchRows,
	patchStocktakeItemInList,
	resolveBestCurrentWarehouseBatchRows,
} from './StocktakeDetailHelpers';

// Batch dialog state for one stocktake item: opening loads fresh warehouse batches,
// saving updates the item optimistically and rolls back when the mutation fails.
const useStocktakeBatchDialog = ({ items, setItems, tenant, modifyStocktakeItemMutation }) => {
	const apolloClient = useApolloClient();
	const activeStocktakeItemRef = useRef(null);

	const [activeBatchItem, setActiveBatchItem] = useState(null);
	const [batchDialogRows, setBatchDialogRows] = useState([]);
	const [batchDialogWarehouseRows, setBatchDialogWarehouseRows] = useState([]);
	const [batchDialogError, setBatchDialogError] = useState('');
	const [isFetchingWarehouseBatches, setIsFetchingWarehouseBatches] = useState(false);

	useEffect(() => {
		activeStocktakeItemRef.current = activeBatchItem?.itemId || null;
	}, [activeBatchItem?.itemId]);

	const openBatchDialog = useCallback((item, barcode) => {
		if (!item) {
			return;
		}

		const timezone = getWarehouseProductTimezone(item.warehouseProduct, tenant?.timezone);
		const itemId = item.id || item._id || null;
		const latestItem = itemId ? (items.find(existing => (existing.id || existing._id) === itemId) || item) : item;
		const currentWarehouseRows = resolveBestCurrentWarehouseBatchRows({
			item: latestItem,
			currentWarehouseRows: getCurrentWarehouseBatchRows(latestItem, timezone),
		});
		setBatchDialogWarehouseRows(currentWarehouseRows);
		setBatchDialogRows(getStocktakeBatchRowsForDialog(latestItem, currentWarehouseRows, timezone));
		setBatchDialogError('');
		setIsFetchingWarehouseBatches(false);
		setActiveBatchItem({
			itemId,
			fallbackItem: {
				...latestItem,
				quantity: valueExists(latestItem.quantity) ? latestItem.quantity : null,
			},
			barcode,
		});
		if (!latestItem?.warehouseProduct?.id) {
			return;
		}

		setIsFetchingWarehouseBatches(true);
		return apolloClient.query({
			query: GET_WAREHOUSE_PRODUCT_EXPIRY_BATCHES,
			variables: { id: latestItem.warehouseProduct.id },
			fetchPolicy: 'network-only',
		})
			.then(({ data }) => {
				if (activeStocktakeItemRef.current !== itemId) {
					return;
				}

				const freshRows = normalizeStocktakeBatchRows(data?.getWarehouseProduct?.expiryBatches || [], timezone);
				const resolvedFreshRows = resolveBestCurrentWarehouseBatchRows({
					item: latestItem,
					currentWarehouseRows: freshRows,
				});
				setBatchDialogWarehouseRows(resolvedFreshRows);
				setBatchDialogRows(getStocktakeBatchRowsForDialog(latestItem, resolvedFreshRows, timezone));
			})
			.catch(() => {})
			.finally(() => {
				setIsFetchingWarehouseBatches(false);
			});
	}, [apolloClient, items, tenant?.timezone]);

	const closeBatchDialog = useCallback(() => {
		setActiveBatchItem(null);
		setBatchDialogRows([]);
		setBatchDialogWarehouseRows([]);
		setBatchDialogError('');
		setIsFetchingWarehouseBatches(false);
	}, []);

	const handleSaveBatches = useCallback(async (targetItem, rows) => {
		if (!targetItem?.id && !targetItem?._id) {
			return;
		}

		const targetItemId = targetItem.id || targetItem._id;
		const timezone = getWarehouseProductTimezone(targetItem.warehouseProduct, tenant?.timezone);
		const normalizedRows = normalizeStocktakeBatchRows(rows || [], timezone);

		const hasDateWithoutQuantity = normalizedRows.some(row => (row.expiryDate || row.supplierBatchCode) && !Number(row.quantity));
		if (hasDateWithoutQuantity) {
			setBatchDialogError('Each batch with date/code must include quantity.');
			return;
		}

		const hasInvalidDate = normalizedRows.some(row => row.expiryDate && !moment(row.expiryDate, 'YYYY-MM-DD', true).isValid());
		if (hasInvalidDate) {
			setBatchDialogError('Use YYYY-MM-DD format for expiry dates.');
			return;
		}

		const countedQuantity = normalizedRows.reduce((acc, row) => acc + row.quantity, 0);
		const previousItem = items.find(item => (item.id || item._id) === targetItemId) || targetItem;
		const previousQuantity = valueExists(previousItem.quantity) ? previousItem.quantity : null;
		const previousCountedExpiryBatches = previousItem.countedExpiryBatches;
		const localCountedRows = normalizedRows.map(row => ({
			quantity: row.quantity,
			quantityOnHand: row.quantity,
			expiryDate: row.expiryDate || null,
			supplierBatchCode: row.supplierBatchCode || null,
		}));
		const mutationRows = normalizedRows.map(row => ({
			quantity: row.quantity,
			expiryDate: serializeDateKeyForTimezone(row.expiryDate, timezone),
			supplierBatchCode: row.supplierBatchCode || null,
		}));

		setBatchDialogError('');

		setItems(prevItems => patchStocktakeItemInList(prevItems, targetItemId, {
			quantity: countedQuantity,
			countedExpiryBatches: localCountedRows,
		}));

		try {
			await modifyStocktakeItemMutation({
				variables: {
					input: {
						id: targetItemId,
						quantity: countedQuantity,
						countedExpiryBatches: mutationRows,
					},
				},
			});
			Toast.show('Batches saved');
			closeBatchDialog();
		}
		catch (error) {
			setItems(prevItems => patchStocktakeItemInList(prevItems, targetItemId, {
				quantity: previousQuantity,
				countedExpiryBatches: previousCountedExpiryBatches,
			}));
			setBatchDialogError('Unable to save batches.');
		}
	}, [closeBatchDialog, items, modifyStocktakeItemMutation, setItems, tenant?.timezone]);

	const resolvedActiveItem = useMemo(() => {
		if (!activeBatchItem) {
			return null;
		}
		if (activeBatchItem.itemId) {
			const latestItem = items.find(item => (item.id === activeBatchItem.itemId || item._id === activeBatchItem.itemId));
			if (latestItem) {
				const latestQuantity = valueExists(latestItem.quantity) ? latestItem.quantity : null;
				return {
					...latestItem,
					quantity: latestQuantity,
				};
			}
		}
		const fallbackItem = activeBatchItem.fallbackItem || null;
		return fallbackItem;
	}, [activeBatchItem, items]);

	return {
		activeBatchItem,
		batchDialogRows,
		setBatchDialogRows,
		batchDialogWarehouseRows,
		batchDialogError,
		isFetchingWarehouseBatches,
		openBatchDialog,
		closeBatchDialog,
		handleSaveBatches,
		resolvedActiveItem,
	};
};

export default useStocktakeBatchDialog;
